import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from 'lib/utils';

/**
 * Radix Tooltip ignores touch by design (shadcn-ui/ui#86), so we swap in a Radix
 * Popover on coarse-pointer devices. Same exported API either way.
 */

const COARSE_POINTER_QUERY = '(pointer: coarse)';

const canMatchMedia = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const getCoarsePointer = () =>
  canMatchMedia() ? window.matchMedia(COARSE_POINTER_QUERY).matches : false;

// Read synchronously on first render: deferring to an effect would remount every
// tooltip on touch right after first paint.
const useIsTouch = (): boolean => {
  const [isTouch, setIsTouch] = React.useState(getCoarsePointer);

  React.useEffect(() => {
    if (!canMatchMedia()) return;
    const query = window.matchMedia(COARSE_POINTER_QUERY);
    if (typeof query.addEventListener !== 'function') return;

    const onChange = (event: MediaQueryListEvent) => setIsTouch(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return isTouch;
};

// Keeps Trigger and Content on the same primitive as their Root.
const TouchModeContext = React.createContext(false);

const TooltipProvider = TooltipPrimitive.Provider;

type TooltipProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
> &
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root>;

const Tooltip = ({ children, ...props }: TooltipProps): JSX.Element => {
  const isTouch = useIsTouch();
  const Root = isTouch ? PopoverPrimitive.Root : TooltipPrimitive.Root;

  return (
    <TouchModeContext.Provider value={isTouch}>
      <Root {...props}>{children}</Root>
    </TouchModeContext.Provider>
  );
};

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>((props, ref) => {
  const isTouch = React.useContext(TouchModeContext);
  const Trigger = isTouch ? PopoverPrimitive.Trigger : TooltipPrimitive.Trigger;

  return <Trigger ref={ref} {...props} />;
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

// The `max-w` caps at a readable measure *and* at the width the positioner
// reports available, so a tooltip near a viewport edge shrinks instead of being
// clipped. The `100vw` fallback keeps the declaration valid before that var
// exists.
const contentClassName = (primitive: 'tooltip' | 'popover') =>
  cn(
    'z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md break-words',
    primitive === 'tooltip'
      ? 'max-w-[min(20rem,var(--radix-tooltip-content-available-width,100vw))] origin-[--radix-tooltip-content-transform-origin]'
      : 'max-w-[min(20rem,var(--radix-popover-content-available-width,100vw))] origin-[--radix-popover-content-transform-origin]',
    'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
  );

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => {
  const isTouch = React.useContext(TouchModeContext);

  if (isTouch) {
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className={cn(contentClassName('popover'), className)}
          {...props}
        />
      </PopoverPrimitive.Portal>
    );
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(contentClassName('tooltip'), className)}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, useIsTouch };
